import React, { Fragment } from 'react';
import { BasicPrivilegeDependency } from '../PrivilegeConfig.types';
import { ExtendedInfoSVG } from '../../../_lib/components/ExtendedInfo';
import { ExtendedInfoModifier } from '../../../_lib/components/ExtendedInfo/partials/ExtendedInfo';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { SVGIcon } from '../../../components/atoms/SVG';
import { PageTypeTitle } from '../../../_lib/components/PageType';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Strong } from '../../../components/atoms/Strong/Strong';

type RequiredPrivilegeHints = {
  missingPrivilegeIconId: SVGIcon;
  missingPrivilegeIconModifier?: ExtendedInfoModifier;
  missingPrivilegeHelpText: string;
  /**
   * the list of required basic privileges (pairs of privilege+pageType).
   */
  requiredBasicPrivileges: BasicPrivilegeDependency[];
  /**
   * the translated labels of all parent additional privileges.
   */
  parentAdditionalPrivilegeLabels: string[];
};

/**
 * Renders a comma-seperated list of all (required) parent additional privileges
 * and the required basic privilege dependencies (missing or non-missing).
 * @param requiredBasicPrivileges List of required basic privileges.
 * @param parentAdditionalPrivilegeLabels Translated labels of all parent additional privileges
 *                                        (passed from parent components).
 * @param t i18n.
 */
export function RequiredPrivilegeHints({
  missingPrivilegeIconId,
  missingPrivilegeIconModifier,
  missingPrivilegeHelpText,
  requiredBasicPrivileges,
  parentAdditionalPrivilegeLabels,
}: RequiredPrivilegeHints) {
  const { t } = useSonTranslation('PrivilegeConfig');

  // prepare list of required parent additional privileges as plain text:
  const requiredAdditionalPrivilegeLabels: any = parentAdditionalPrivilegeLabels.map(parentPrivilegeLabel => (
    <Fragment key={parentPrivilegeLabel}>{parentPrivilegeLabel}</Fragment>
  ));

  // prepare list of required basic privileges (which may have a warning tooltip when not granted to the role):
  const requiredBasicPrivilegeHints: any = requiredBasicPrivileges.map(requiredPrivilege => (
    <Fragment key={requiredPrivilege.dependency.pageType}>
      {requiredPrivilege.isMissing ? (
        <ExtendedInfoSVG
          helpText={missingPrivilegeHelpText}
          tooltipPlacement={TooltipPlacement.TOP}
          svgSpriteID={missingPrivilegeIconId}
          modifier={missingPrivilegeIconModifier}
        />
      ) : null}
      {`${t(`headline.${requiredPrivilege.dependency.privilege.toLowerCase()}` as any)}: `}
      <PageTypeTitle pageType={requiredPrivilege.dependency.pageType} />
    </Fragment>
  ));

  // merge all hints as comma-separated span:
  const mergedRequiredPrivilegeHints = [...requiredAdditionalPrivilegeLabels, ...requiredBasicPrivilegeHints];
  return mergedRequiredPrivilegeHints.length > 0 ? (
    <>
      {/* BEWARE: this must be a <span> because it's part of a <label>! */}
      <span className="privilege-config__required-privileges font-regular">
        <Strong>{t('requiredPrivilegesHint')}:&nbsp;</Strong>
        {mergedRequiredPrivilegeHints.map((requiredPrivilege, index, iteratedList) => (
          <Fragment key={requiredPrivilege.key}>
            {requiredPrivilege}
            {index < iteratedList.length - 1 ? `,\u0020` : null}
          </Fragment>
        ))}
      </span>
    </>
  ) : null;
}
